import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiArrowRight } from 'react-icons/fi';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../utils/firebase';
import ProductCard from '../components/ProductCard';
import LoadingSpinner from '../components/LoadingSpinner';
import { useProducts } from '../hooks/useProducts';
import { useHeroReveal, useScrollReveal } from '../hooks/useGsapMotion';

import mobileIcon from '../assets/category/mobile-phone.png';
import computerIcon from '../assets/category/computer.png';
import smartwatchIcon from '../assets/category/smartwatch.png';
import cameraIcon from '../assets/category/camera.png';
import headphonesIcon from '../assets/category/headphones.png';
import gamingIcon from '../assets/category/gaming.png';

import fastDeliveryIcon from '../assets/contact/fast-delivery.png';
import customerServiceIcon from '../assets/contact/customer_sevice.png';
import moneyBackIcon from '../assets/contact/check-mark.png';

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slides, setSlides] = useState([]);
  const [slidesLoading, setSlidesLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const pageRef = useRef(null);

  const { products: flashSales, loading: flashLoading } = useProducts({ limit: 8, orderBy: 'createdAt' });
  const { products: bestSelling, loading: bestLoading } = useProducts({ limit: 4, orderBy: 'sales' });

  useHeroReveal(pageRef, [slidesLoading]);
  useScrollReveal(pageRef, [flashLoading, bestLoading, categoriesLoading, slides.length]);

  useEffect(() => {
    const fetchSlides = async () => {
      const defaultSlides = [
        {
          id: 1,
          title: 'Premium Electronics',
          subtitle: 'Upgrade your setup with exclusive deals',
          image: 'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=1200',
          link: '/products'
        },
        {
          id: 2,
          title: 'Lifestyle Collection',
          subtitle: 'Handpicked essentials for modern living',
          image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1200',
          link: '/products'
        },
        {
          id: 3,
          title: 'Gaming Zone',
          subtitle: 'Performance gear for serious players',
          image: 'https://images.unsplash.com/photo-1542751371-adc38448a05e?w=1200',
          link: '/category/gaming'
        }
      ];

      try {
        setSlidesLoading(true);
        const slidesQuery = query(collection(db, 'carouselSlides'), where('isActive', '==', true));
        const slidesSnapshot = await getDocs(slidesQuery);
        const slidesData = slidesSnapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .sort((a, b) => (a.order || 0) - (b.order || 0));

        setSlides(slidesData.length > 0 ? slidesData : defaultSlides);
      } catch (error) {
        console.error('Error fetching slides:', error);
        setSlides(defaultSlides);
      } finally {
        setSlidesLoading(false);
      }
    };

    fetchSlides();
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const querySnapshot = await getDocs(collection(db, 'categories'));
        const categoriesData = querySnapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .sort((a, b) => (a.order || 0) - (b.order || 0));

        const formatted = categoriesData.map((category) => ({
          name: category.name,
          icon: category.icon || getDefaultCategoryIcon(category.slug),
          link: `/category/${category.slug}`
        }));

        setCategories(formatted.length > 0 ? formatted : getDefaultCategories());
      } catch (error) {
        console.error('Error fetching categories:', error);
        setCategories(getDefaultCategories());
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    if (!slides.length || slidesLoading) return undefined;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides, slidesLoading]);

  const getDefaultCategoryIcon = (slug) => {
    const iconMap = {
      'mobile-phones': mobileIcon,
      'computer-accessories': computerIcon,
      'smart-devices': smartwatchIcon,
      photography: cameraIcon,
      'audio-sound': headphonesIcon,
      gaming: gamingIcon
    };
    return iconMap[slug] || computerIcon;
  };

  const getDefaultCategories = () => [
    { name: 'Computer Accessories', icon: computerIcon, link: '/category/computer-accessories' },
    { name: 'Audio & Sound', icon: headphonesIcon, link: '/category/audio-sound' },
    { name: 'Smart Devices', icon: smartwatchIcon, link: '/category/smart-devices' },
    { name: 'Power & Storage', icon: computerIcon, link: '/category/power-storage' },
    { name: 'Home Appliances', icon: cameraIcon, link: '/category/home-appliances' },
    { name: 'Gaming', icon: gamingIcon, link: '/category/gaming' }
  ];

  const nextSlide = () => {
    if (!slides.length) return;
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    if (!slides.length) return;
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div ref={pageRef}>
      <section className="sc-container pb-6 pt-6 lg:pb-10 lg:pt-8">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
          <aside className="sc-soft-card lg:col-span-3 h-[280px] sm:h-[320px] lg:h-[340px] p-4 flex flex-col" data-hero-item>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-base font-bold text-[#1A1A1A]">Browse Category</h3>
              <span className="sc-chip">Popular</span>
            </div>
            {categoriesLoading ? (
              <div className="flex-1 flex items-center justify-center">
                <LoadingSpinner />
              </div>
            ) : (
              <ul className="flex-1 space-y-1 overflow-y-auto pr-1">
                {categories.map((category, index) => (
                  <li key={`${category.name}-${index}`}>
                    <Link
                      to={category.link}
                      className="group flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] font-semibold text-slate-600 transition hover:bg-red-50 hover:text-[#DB4444]"
                    >
                      <img src={category.icon} alt={category.name} className="h-5 w-5 object-contain" />
                      <span>{category.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </aside>

          <div className="relative h-[280px] sm:h-[320px] lg:h-[340px] overflow-hidden rounded-[1.25rem] bg-[#1A1A1A] lg:col-span-9" data-hero-item>
            {slidesLoading ? (
              <div className="flex h-full items-center justify-center">
                <LoadingSpinner size="large" />
              </div>
            ) : (
              <>
                {slides.map((slide, index) => (
                  <div
                    key={slide.id}
                    className={`absolute inset-0 transition-opacity duration-700 ${
                      index === currentSlide ? 'opacity-100' : 'pointer-events-none opacity-0'
                    }`}
                  >
                    <img src={slide.image} alt={slide.title} className="h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/30 to-black/20" />
                    <div className="absolute bottom-5 left-5 right-5 max-w-md sm:left-8 sm:right-auto sm:bottom-7">
                      <p className="mb-2 text-xs font-bold uppercase tracking-[0.24em] text-red-200">
                        Featured Campaign
                      </p>
                      <h1 className="text-2xl font-black leading-tight text-white sm:text-3xl lg:text-4xl">
                        {slide.subtitle}
                      </h1>
                      <p className="mt-1.5 text-xs sm:text-sm font-medium text-slate-200">{slide.title}</p>
                      <Link to={slide.link} className="sc-btn-primary mt-4 inline-flex items-center gap-2 !py-2.5 !px-5 text-sm">
                        Shop now
                        <FiArrowRight size={14} />
                      </Link>
                    </div>
                  </div>
                ))}

                <button
                  onClick={prevSlide}
                  className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full border border-white/40 bg-white/10 p-2 text-white backdrop-blur transition hover:bg-white/20"
                >
                  <FiArrowLeft size={16} />
                </button>
                <button
                  onClick={nextSlide}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full border border-white/40 bg-white/10 p-2 text-white backdrop-blur transition hover:bg-white/20"
                >
                  <FiArrowRight size={16} />
                </button>

                <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2">
                  {slides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`h-2.5 rounded-full transition-all ${
                        index === currentSlide ? 'w-8 bg-white' : 'w-2.5 bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white py-14" data-reveal>
        <div className="sc-container">
          <div className="mb-7 flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-[0.24em] text-[#DB4444]">Limited time</p>
              <h2 className="text-3xl font-black text-[#1A1A1A]">Flash Sales</h2>
            </div>
            <Link to="/flash-sales" className="sc-btn-primary inline-flex items-center gap-2">
              View all
              <FiArrowRight size={14} />
            </Link>
          </div>

          {flashLoading ? (
            <LoadingSpinner />
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4" data-stagger>
              {flashSales.map((product) => (
                <ProductCard key={product.id} product={product} showDiscount />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="sc-container py-16" data-reveal>
        <div className="mb-8 flex items-center justify-between gap-3">
          <h2 className="text-3xl font-black text-[#1A1A1A]">Shop by Category</h2>
          <Link to="/products" className="text-sm font-bold text-[#DB4444]">
            Explore all
          </Link>
        </div>

        {categoriesLoading ? (
          <LoadingSpinner />
        ) : (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6" data-stagger>
            {categories.map((category, index) => (
              <Link
                key={`browse-${index}`}
                to={category.link}
                className="group sc-soft-card flex min-h-[140px] flex-col items-center justify-center p-4 text-center transition hover:border-[#DB4444] hover:bg-[#DB4444]"
                data-stagger-item
              >
                <img
                  src={category.icon}
                  alt={category.name}
                  className="mb-3 h-10 w-10 object-contain transition duration-300 group-hover:scale-105"
                />
                <span className="text-sm font-semibold text-slate-700 transition group-hover:text-white">
                  {category.name}
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className="sc-container pb-16" data-reveal>
        <div className="mb-7 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.24em] text-[#DB4444]">Top picks</p>
            <h2 className="text-3xl font-black text-[#1A1A1A]">Best Selling Products</h2>
          </div>
          <Link to="/best-selling" className="sc-btn-ghost inline-flex items-center gap-2">
            See more
            <FiArrowRight size={14} />
          </Link>
        </div>

        {bestLoading ? (
          <LoadingSpinner />
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4" data-stagger>
            {bestSelling.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </section>

      <section className="bg-white py-16" data-reveal>
        <div className="sc-container grid grid-cols-1 gap-6 md:grid-cols-3" data-stagger>
          <article className="sc-soft-card p-6 text-center" data-stagger-item>
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#DB4444]">
              <img src={fastDeliveryIcon} alt="Fast delivery" className="h-7 w-7 brightness-0 invert" />
            </div>
            <h3 className="text-sm font-extrabold uppercase tracking-wider text-[#1A1A1A]">
              Free and fast delivery
            </h3>
            <p className="mt-2 text-sm text-slate-500">Free delivery for all orders over ৳140</p>
          </article>

          <article className="sc-soft-card p-6 text-center" data-stagger-item>
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#DB4444]">
              <img src={customerServiceIcon} alt="Support" className="h-7 w-7 brightness-0 invert" />
            </div>
            <h3 className="text-sm font-extrabold uppercase tracking-wider text-[#1A1A1A]">
              24/7 customer support
            </h3>
            <p className="mt-2 text-sm text-slate-500">Friendly support team always ready to help you.</p>
          </article>

          <article className="sc-soft-card p-6 text-center" data-stagger-item>
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#DB4444]">
              <img src={moneyBackIcon} alt="Money back" className="h-7 w-7 brightness-0 invert" />
            </div>
            <h3 className="text-sm font-extrabold uppercase tracking-wider text-[#1A1A1A]">
              Money back guarantee
            </h3>
            <p className="mt-2 text-sm text-slate-500">Easy return and refund policy for peace of mind.</p>
          </article>
        </div>
      </section>
    </div>
  );
};

export default Home;
